import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { showErrorToast, showSuccessToast } from "../../utils/Toast";
import {
  Button,
  TextField,
  FormControl,
  Grid,
  Breadcrumbs,
  Stack,
  Typography,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { HomeRounded } from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

const UpdateFabrecatedItem = () => {
  const [fields, setFields] = useState([{ quantity: "", poitems_id: "" }]);
  const [endProduct, setEndProduct] = useState("");
  const [poItems, setPoItems] = useState([]);
  const { id } = useParams();
  console.log(id, "JObID");
  const navigate = useNavigate();
  const location = useLocation();
  const { item } = location.state || {};

  // console.log(item, ")()()()()");
  const isUpdateMode = Boolean(item);

  // W-23: section, length and grade are not sent. The server writes only the fields it receives
  // (updatefabricateditems, backend PR #120), so it keeps them; sending them could overwrite a newer
  // correction with a stale copy.
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false); // blocks a second save before the state update lands
  // The item this page currently shows. A save response for another item (the user moved on) or after
  // the page closed is ignored.
  const activeItemId = useRef(item?.id);
  activeItemId.current = item?.id;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isUpdateMode) {
      setEndProduct(item?.name || "");
      setFields([
        { quantity: item?.quantity || "", poitems_id: item?.poitems_id || "" },
      ]);
    }
  }, [isUpdateMode, item]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingRef.current || !item) return;

    if (!endProduct) {
      toast.error("End Product is required", {
        position: "top-right",
      });
      return;
    }

    const row = fields[0] || { quantity: "", poitems_id: "" };
    for (const item of [row]) {
      if (
        item.quantity === "" ||
        isNaN(item.quantity) ||
        item.quantity <= 0 ||
        !item.poitems_id
      ) {
        toast.error("Please fill in all fields for each item", {
          position: "top-right",
        });
        return;
      }
    }

    // Snapshot of the validated form, taken before the request: what is sent is what was validated
    // (the form is also locked while saving).
    const target = { id: item.id, jobid: item.jobid, name: endProduct };
    const payload = {
      name: endProduct,
      job_Id: target.jobid,
      quantity: row.quantity,
      poitems_id: row.poitems_id,
    };
    const stillHere = () =>
      mounted.current && String(activeItemId.current) === String(target.id);

    savingRef.current = true;
    setSaving(true);
    try {
      await ApiCall.put(
        `/fabricated-items/update-fabricated-item/${target.id}`,
        payload
      );
      if (!stillHere()) {
        // The user has moved on: report it, but do not navigate away from where they are now.
        showSuccessToast(`"${target.name}" was updated.`);
        return;
      }
      showSuccessToast(`"${target.name}" was updated.`);
      navigate(`/detail-Job/${target.jobid}`);
    } catch (error) {
      console.error("An error occurred:", error);
      const body = error.response?.data;
      const reason =
        (typeof body?.data === "string" && body.data) ||
        body?.message ||
        error.message ||
        "unknown error";
      // Shown even if the user has left this page or switched item: a failed save is never silent.
      showErrorToast(`"${target.name}" was NOT updated: ${reason}`);
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const handleFieldChange = (index, fieldName, value) => {
    if (savingRef.current) return;
    setFields(fields.map((f, i) => (i === index ? { ...f, [fieldName]: value } : f)));
  };

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await ApiCall.get(
          `/fabricated-items/get-poitem-perjob/${item.jobid}`
        );
        setPoItems(res.data.data);
      } catch (error) {
        console.error(error);
      }
    };

    if (item?.jobid) fetchItems();
  }, [item?.jobid]);

  return (
    <>
      <div>
        <Helmet>
          <title>Update Fabricated Item | SFM</title>
        </Helmet>
        <container>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={5}
          >
            <Breadcrumbs aria-label="breadcrumb">
              <Stack direction="row" alignItems="center" spacing={1}>
                <HomeRounded color="inherit" />
                <Typography variant="body1" color="textPrimary">
                  {isUpdateMode
                    ? "/ Update Fabricated Items"
                    : "/ Create Fabricated Items"}
                </Typography>
              </Stack>
            </Breadcrumbs>
          </Stack>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid
                item
                xs={3}
                style={{ marginLeft: "30%", marginBottom: "2%" }}
              >
                <FormControl fullWidth>
                  <TextField
                    onChange={(event) => setEndProduct(event.target.value)}
                    name="endProduct"
                    label="End Product"
                    disabled={saving}
                    required
                    value={endProduct}
                  />
                </FormControl>
              </Grid>
            </Grid>
            {fields.slice(0, 1).map((field, index) => (
              <Grid container spacing={2} alignItems="center" key={index}>
                {/* W-23: an update saves one item (row 1 only), so no add/remove-row controls. */}
                <Grid item xs={2} />
                <Grid item xs={3} style={{ marginBottom: "5px" }}>
                  <FormControl fullWidth>
                    <TextField
                      onChange={(event) =>
                        handleFieldChange(index, "quantity", event.target.value)
                      }
                      name={`quantity-${index}`}
                      label="Quantity"
                      disabled={saving}
                      value={field.quantity}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={3} style={{ marginBottom: "5px" }}>
                  <FormControl fullWidth>
                    <InputLabel htmlFor={`label-${index}`}>
                      Select POItems
                    </InputLabel>
                    <Select
                      disabled={saving}
                      value={field.poitems_id}
                      onChange={(event) =>
                        handleFieldChange(
                          index,
                          "poitems_id",
                          event.target.value
                        )
                      }
                      id={`label-${index}`}
                    >
                      {poItems.length === 0 && (
                        <MenuItem disabled value="">
                          No Item Found
                        </MenuItem>
                      )}
                      {poItems.map((dataItem) => (
                        <MenuItem key={dataItem.id} value={dataItem.id}>
                          {dataItem.itemName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            ))}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || !isUpdateMode}
            >
              {saving ? "Saving..." : isUpdateMode ? "Update" : "Create"}
            </Button>
          </form>
        </container>
      </div>
    </>
  );
};

export default UpdateFabrecatedItem;
